import { useMemo } from 'react';
import type { CreditBalance, CreditType } from '@/services/endpoints/credits';
import { deriveCreditGate } from '../utils/deriveCreditGate';
import { useCreditBalance, type UseCreditBalanceResult } from './useCreditBalance';

type UseCreditFeatureCheckArgs = {
  type: CreditType;
  skip?: boolean;
};

export type UseCreditFeatureCheckResult = {
  canUseFeature: boolean;
  isOutOfCredits: boolean;
  isFeatureUnavailable: boolean;
  isLoading: boolean;
  available: number;
  reserved: number;
  balance: CreditBalance | null;
  isKnown: boolean;
  error: UseCreditBalanceResult['error'];
};

export const useCreditFeatureCheck = ({ type, skip = false }: UseCreditFeatureCheckArgs): UseCreditFeatureCheckResult => {
  const { balance, available, reserved, isKnown, isLoading, error, isFeatureUnavailable } = useCreditBalance({ type, skip });

  const gate = useMemo(
    () =>
      deriveCreditGate({
        isFeatureUnavailable,
        isLoading,
        balance,
        available,
      }),
    [available, balance, isFeatureUnavailable, isLoading]
  );

  return {
    canUseFeature: gate.canUseFeature,
    isOutOfCredits: gate.isOutOfCredits,
    isFeatureUnavailable: gate.isFeatureUnavailable,
    isLoading,
    available,
    reserved,
    balance,
    isKnown,
    error,
  };
};
